import {
    Box,
    Button,
    Fab,
    FormControl,
    FormControlLabel,
    Icon,
    InputAdornment,
    Radio,
    RadioGroup,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { FormEvent } from 'react';
import { connect } from 'react-redux';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { If } from 'app/components';
import { CobranderSelector } from 'app/components/form-controls/cobrander-selector';
import { OpposingPatternSelector } from 'app/components/form-controls/opposing-pattern-selector';
import { PatternTypeSelector } from 'app/components/pattern-type-selector';
import { CrisRequestType } from 'app/constants/cris-request-type';
import { MatchType } from 'app/constants/match-type';
import { TimingConstants } from 'app/constants/timing-constants';
import { LocationMatchType } from 'app/enum';
import { Store } from 'app/model';
import { AsyncSearchResponse } from 'app/model/es/async-search.response';
import { ListOptions } from 'app/model/list-options';
import { MidStats } from 'app/model/mid-stats';
import { MidTuple } from 'app/model/mid-tuple';
import { SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { searchService } from 'app/services/search-service';
import { RootState, store } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';
import { WithEsId } from 'app/type/with-es-id';
import { localPreferenceManager } from 'app/utils/local-preference-manager';

import { ElasticRegexInfo } from './elastic-regex-info';
import { MatchTable } from './pattern-form/match-table';
import { PatternTuples } from './pattern-tuples';
import { startLoading, stopLoading } from '../../top-loader/loader.slice';
import { TupleFilters } from '../../tuples/tuple-filters';
import { selectSpendSort, setSpendSort } from '../../tuples/tuple.slice';

interface PatternFormProps {
    data: ModelWithoutId<SeamPattern>;
    correction: boolean;
    hideActions?: boolean;
    targetStoreId?: number;
    initialStats?: MidStats;
    seamRequest?: SeamRequest;

    onChange: (data: ModelWithoutId<SeamPattern>) => void;
    onClone?: () => void;
    onSubmit?: (createNew: boolean) => void;
    onCancel?: () => void;
    onSearch: (
        opts: ListOptions,
        lastSort?: Array<number | string>,
        skipFilters?: boolean,
        ignoreDeactivated?: boolean,
    ) => Observable<AsyncSearchResponse<MidTuple>>;
    spendSort: boolean;
    setSpendSort: (update: boolean) => void;
    store: Store | undefined;
}

interface PatternFormState {
    dirty: boolean;
    searched: boolean;
    formVisible: boolean;
    withStats: 'none' | 'pattern' | 'store' | 'view';
    tupleTable: {
        tuples: Array<Array<WithEsId<MidTuple>>>;
        sortModel: Array<{ field: keyof MidTuple; sort: 'asc' | 'desc' | undefined }>;
        totalTrx: number;
        totalSpend: number;
    };
    sort?: Array<number | string>;
    ignoreDeactivated: boolean;
}

class PatternForm extends React.Component<PatternFormProps, PatternFormState> {
    private searchSubscription?: Subscription;
    private nextLoadIndex = 0;

    public constructor(props: PatternFormProps | Readonly<PatternFormProps>) {
        super(props);

        this.state = {
            dirty: true,
            searched: false,
            formVisible: true,
            withStats: 'none',
            tupleTable: {
                tuples: [],
                sortModel: [],
                totalTrx: 0,
                totalSpend: 0,
            },
            ignoreDeactivated: false,
        };

        // binding allows us to use this directly in template without doing '() => this.handleSubmit()'
        this.handleChangeEvt = this.handleChangeEvt.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.doSort = this.doSort.bind(this);
        this.doDownload = this.doDownload.bind(this);
        this.loadMore = this.loadMore.bind(this);
    }

    public componentDidMount() {
        if (this.props.spendSort) {
            this.state.tupleTable.sortModel = [{ field: 'totalSpend', sort: 'desc' }];
        }
    }

    public handleCreateSubmit(evt: FormEvent, createNew: boolean): void {
        evt.preventDefault();
        this.props.onSubmit?.(createNew);
    }

    public handleChangeEvt(evt: { target: { value: unknown; name?: string } }): void {
        if (!evt.target.name) {
            throw new Error('Control must have name defined');
        }

        let value = evt.target.value;
        if ('mccPattern' === evt.target.name) {
            value = '' === value ? [] : (value as string).split(',');
        }

        if ('matchOperator' === evt.target.name) {
            localPreferenceManager.setPreferredMatchType(value as MatchType);
        }
        if ('locationOperator' === evt.target.name) {
            localPreferenceManager.setPreferredLocationMatchType(value as LocationMatchType);
        }

        this.handleChange(evt.target.name, value);
    }

    public handleChange(name: string, value: unknown): void {
        this.setState({ dirty: true });

        if ('type' === name) {
            this.props.onChange({
                ...this.props.data,
                type: value as CrisRequestType,
            });

            return;
        }
        if ('opposingId' === name) {
            this.props.onChange({
                ...this.props.data,
                opposingId: value as string,
            });

            return;
        }

        if ('noReplacement' === name) {
            this.props.onChange({
                ...this.props.data,
                noReplacement: value as boolean,
            });

            return;
        }

        switch (name) {
            case 'descPattern':
            case 'midPattern':
                value = this.normalizePattern(value as string);
                break;
            case 'cityPattern':
            case 'statePattern':
            case 'zipPattern':
            case 'countryPattern':
                value = (value as string).toUpperCase();
                break;
            default:
            // noop
        }

        this.props.onChange({
            ...this.props.data,
            data: {
                ...this.props.data.data,
                [name]: value as string,
            },
        });
    }

    public render(): React.ReactNode {
        const seamPattern = this.props.data;
        const { seamRequest } = this.props;

        return (
            <form onSubmit={(evt) => evt.preventDefault()}>
                <Box display='flex'>
                    {true === this.props.hideActions || !this.state.formVisible ? (
                        ''
                    ) : (
                        <Box display='flex' alignItems='center'>
                            <PatternTypeSelector
                                value={seamPattern.type}
                                onChange={this.handleChangeEvt}
                                disabled={!this.canChangeType()}
                                startAdornment={
                                    <InputAdornment position='start'>
                                        <Tooltip title='Type can be changed only during creation of new map pattern.'>
                                            <Icon color='info'>help_outline</Icon>
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            />
                            <CobranderSelector
                                name='cobrander'
                                className='m-2'
                                size='small'
                                variant='outlined'
                                sx={{ minWidth: '10em', mr: 3 }}
                                value={seamPattern.data.cobrander}
                                onChange={this.handleChangeEvt}
                            />
                            <If condition={!!seamRequest}>
                                <OpposingPatternSelector
                                    name='opposingId'
                                    value={seamPattern.opposingId}
                                    onChange={this.handleChangeEvt}
                                    size='small'
                                    seamRequest={seamRequest!}
                                />
                            </If>
                            {this.renderSupersedeElements(seamPattern)}
                        </Box>
                    )}
                </Box>
                {seamPattern.noReplacement ? (
                    <Box>
                        <Typography variant='body1'>
                            This request will only deactivate the deployed pattern
                            <strong> {seamPattern.supersedes}</strong>.
                        </Typography>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => this.props.onCancel?.()}
                            style={{ float: 'right' }}
                            size='small'
                        >
                            {seamPattern.id ? 'Close' : 'Cancel'}
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            className='mr-1'
                            onClick={(evt) => this.handleCreateSubmit(evt, false)}
                            style={{ float: 'right' }}
                            size='small'
                        >
                            Save
                        </Button>
                    </Box>
                ) : (
                    <MatchTable
                        data={seamPattern.data}
                        type={seamPattern.type}
                        onChange={(evt) => this.handleChangeEvt(evt)}
                        style={{ display: this.state.formVisible ? '' : 'none' }}
                    >
                        {this.renderMatchActions()}
                        Tuples: {Number(seamPattern.tupleCount || 0).toLocaleString()}
                        {seamPattern.tupleCountRel}&nbsp;
                        {this.state.dirty ? '(outdated)' : ''}&nbsp;
                        <Button
                            variant='contained'
                            color='secondary'
                            size='small'
                            type='submit'
                            onClick={() => this.doSearch()}
                        >
                            Compute
                        </Button>{' '}
                        {this.renderStatsSelection()}
                        <React.Fragment>
                            <Tooltip title='Automatically sort by total spend on search'>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.props.spendSort}
                                            onChange={(event) => this.toggleSpendSort(event.target.checked)}
                                        />
                                    }
                                    label='Spend Sort'
                                />
                            </Tooltip>
                            <Tooltip title='Ignore recently deactivated patterns from search results'>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.ignoreDeactivated}
                                            onChange={(event) =>
                                                this.setState({ ignoreDeactivated: event.target.checked }, () =>
                                                    this.doSearch(),
                                                )
                                            }
                                        />
                                    }
                                    label='Ignore Deactivated'
                                />
                            </Tooltip>
                        </React.Fragment>
                    </MatchTable>
                )}
                {this.state.searched ? (
                    <React.Fragment>
                        <Box display='flex' flexDirection='row' justifyContent='center'>
                            <Tooltip title='Toggle form visibility'>
                                <Fab
                                    color='default'
                                    aria-label='hide'
                                    size='small'
                                    style={{
                                        marginTop: '-20px',
                                        marginBottom: '-20px',
                                        zIndex: 10,
                                    }}
                                    onClick={() => this.togglePatternForm()}
                                >
                                    <Icon color='primary'>
                                        {this.state.formVisible ? 'expand_less' : 'expand_more'}
                                    </Icon>
                                </Fab>
                            </Tooltip>
                        </Box>
                        <TupleFilters
                            onFilter={() => this.doSearch()}
                            onDownload={this.doDownload}
                            store={this.props.store}
                        />
                        <PatternTuples
                            type={seamPattern.type}
                            seamRequest={seamRequest}
                            tuples={this.state.tupleTable.tuples}
                            sortModel={this.state.tupleTable.sortModel}
                            totalTrx={this.state.tupleTable.totalTrx}
                            totalSpend={this.state.tupleTable.totalSpend}
                            onFill={this.handleChange}
                            onSort={this.doSort}
                            onLoadMore={this.loadMore}
                            storeBrandVisibility={!this.props.correction || true === this.props.hideActions}
                            highlightField={this.props.data.data.extractField}
                            highlightPattern={this.props.data.data.extractPattern}
                            highlightChannel={this.props.data.data.subChannel}
                            ignoreDeactivated={this.state.ignoreDeactivated}
                        />
                    </React.Fragment>
                ) : !seamPattern.noReplacement ? (
                    <ElasticRegexInfo />
                ) : (
                    ''
                )}
            </form>
        );
    }

    private toggleSpendSort(checked: boolean): void {
        this.props.setSpendSort(checked);
        if (checked && 0 === this.state.tupleTable.sortModel.length) {
            // if we have empty sort help users out by applying it to current view
            this.state.tupleTable.sortModel = [{ field: 'totalSpend', sort: 'desc' }];
            if (this.state.searched) {
                // trigger another search only if results already being displayed
                this.doSearch();
            }
        }
    }

    private renderMatchActions(): React.ReactNode[] {
        const seamPattern = this.props.data;
        const buttons: React.ReactNode[] = [];
        if (true === this.props.hideActions) {
            // when hide actions is set we do not want to show any buttons.
            return buttons;
        }
        buttons.push(
            <Button
                variant='text'
                key='Close'
                color='secondary'
                onClick={() => this.props.onCancel?.()}
                style={{ float: 'right' }}
                size='small'
            >
                Close
            </Button>,
        );
        if (seamPattern.viewOnly) {
            // for view only close button only
            return buttons;
        }
        if (seamPattern.id) {
            buttons.push(
                <Button
                    variant='outlined'
                    key='Clone'
                    className='mr-1'
                    onClick={() => this.props.onClone?.()}
                    style={{ float: 'right' }}
                    size='small'
                >
                    Clone
                </Button>,
            );
        }
        buttons.push(
            <Button
                variant='outlined'
                key='Savenew'
                color='primary'
                className='mr-1'
                onClick={(evt) => this.handleCreateSubmit(evt, true)}
                style={{ float: 'right' }}
                size='small'
            >
                {' '}
                Save & New
            </Button>,
        );
        buttons.push(
            <Button
                variant='contained'
                key='Save'
                color='primary'
                className='mr-1'
                onClick={(evt) => this.handleCreateSubmit(evt, false)}
                style={{ float: 'right' }}
                size='small'
            >
                {' '}
                Save
            </Button>,
        );

        return buttons;
    }

    private buildListOptions(opts?: ListOptions): ListOptions {
        opts = {
            ...opts,
        };

        const model = this.state.tupleTable.sortModel[0];

        if (model && model.sort) {
            opts.sort = { [model.field]: model.sort };
        }

        return opts;
    }

    private loadAggregation(opts: ListOptions, forceLoad?: true): void {
        const aggOptions = {
            ...opts,
            extraParams: {
                ...opts.extraParams,
                stats: 1,
            },
        };

        switch (this.state.withStats) {
            case 'none':
                this.setState({
                    tupleTable: {
                        ...this.state.tupleTable,
                        totalTrx: 0,
                        totalSpend: 0,
                    },
                });

                return;
            case 'store':
                this.setState({
                    tupleTable: {
                        ...this.state.tupleTable,
                        totalTrx: Number(this.props.initialStats?.trxCount || 0) || -1,
                        totalSpend: Number(this.props.initialStats?.totalSpend || 0) || -1,
                    },
                });

                return;
            case 'pattern':
                if (!forceLoad && (!this.state.dirty || opts.download)) {
                    // we need to update this only when main pattern changed
                    return;
                }

                this.props.onSearch(opts, undefined, true, this.state.ignoreDeactivated).subscribe((value) => {
                    this.setState({
                        tupleTable: {
                            ...this.state.tupleTable,
                            totalTrx: value.extractAggregation('trx', -1),
                            totalSpend: value.extractAggregation('spend', -1),
                        },
                    });
                    this.scheduleAsyncAggsCompletion(value);
                });

                return;
            case 'view':
                // almost same as pattern, but do not skip filters and update on every change
                this.props.onSearch(aggOptions, undefined, true, this.state.ignoreDeactivated).subscribe((value) => {
                    this.setState({
                        tupleTable: {
                            ...this.state.tupleTable,
                            totalTrx: value.extractAggregation('trx', -1),
                            totalSpend: value.extractAggregation('spend', -1),
                        },
                    });
                    this.scheduleAsyncAggsCompletion(value);
                });

                return;
            default:
                throw new Error(`Unexpected withStats value: ${this.state.withStats as string}`);
        }
    }

    private doSearch(opts?: ListOptions): void {
        this.setState(
            {
                searched: true, // show filters and stuff to fix bad filter if there is one
                sort: undefined,
            },
            () => {
                // schedule all searches after state has been updated
                opts = this.buildListOptions(opts);
                this.loadAggregation(opts);

                this.nextLoadIndex = 1; // new search so reset the index, this search handles index 0 so set it to 1

                this.searchSubscription?.unsubscribe();
                this.searchSubscription = this.props
                    .onSearch(opts, this.state.sort, false, this.state.ignoreDeactivated)
                    .subscribe((value) => {
                        if (opts?.download) {
                            return;
                        }
                        this.props.onChange({
                            ...this.props.data,
                            tupleCount: value.response.total.value,
                            tupleCountRel: 'gte' === value.response.total.relation ? '+' : '',
                        });

                        this.setState({
                            dirty: false,
                            searched: true,
                            tupleTable: {
                                ...this.state.tupleTable,
                                tuples: [value.toHitSources()],
                            },
                            sort: value.lastSort(),
                        });
                        store.dispatch(startLoading());
                        this.scheduleAsyncCompletionFetch(0, value);
                    });
            },
        );
    }

    private loadMore(): Observable<unknown> {
        if (!this.state.sort) {
            // if we don't have info of last sort, then either we are at the end or haven't searched yet,
            // so do not load more
            return EMPTY;
        }

        const opts = this.buildListOptions();
        const thisIndex = this.nextLoadIndex++;

        return this.props.onSearch(opts, this.state.sort).pipe(
            tap((value) => {
                const currentTuples = [...this.state.tupleTable.tuples];
                currentTuples[thisIndex] = value.toHitSources();
                this.setState({
                    tupleTable: {
                        ...this.state.tupleTable,
                        tuples: currentTuples,
                    },
                    sort: value.lastSort(),
                });
                store.dispatch(startLoading());
                this.scheduleAsyncCompletionFetch(thisIndex, value);
            }),
        );
    }

    private scheduleAsyncAggsCompletion(response: AsyncSearchResponse<MidTuple>): void {
        if (!response.id || !response.isRunning) {
            return;
        }

        setTimeout(() => {
            searchService.asyncResults<MidTuple>(response.id).subscribe((results) => {
                this.setState({
                    tupleTable: {
                        ...this.state.tupleTable,
                        totalTrx: results.extractAggregation('trx', 0),
                        totalSpend: results.extractAggregation('spend', 0),
                    },
                });
                this.scheduleAsyncAggsCompletion(results);
            });
        }, TimingConstants.ASYNC_RESULTS_INTERVAL);
    }

    private scheduleAsyncCompletionFetch(index: number, response: AsyncSearchResponse<MidTuple>): void {
        if (!response.id || !response.isRunning) {
            store.dispatch(stopLoading());

            // all is loaded
            return;
        }

        setTimeout(() => {
            searchService.asyncResults<MidTuple>(response.id).subscribe((results) => {
                if (0 === index) {
                    // keep updating totals while we are loading initial request
                    this.props.onChange({
                        ...this.props.data,
                        tupleCount: results.response.total.value,
                        tupleCountRel: 'gte' === results.response.total.relation ? '+' : '',
                    });
                }
                const currentTuples = [...this.state.tupleTable.tuples];
                currentTuples[index] = results.toHitSources();
                this.setState({
                    tupleTable: {
                        ...this.state.tupleTable,
                        tuples: currentTuples,
                    },
                    // only set sort if we are the latest search
                    sort: index + 1 === this.nextLoadIndex ? results.lastSort() : this.state.sort,
                });
                this.scheduleAsyncCompletionFetch(index, results);
            });
        }, TimingConstants.ASYNC_RESULTS_INTERVAL);
    }

    private doSort(field?: keyof MidTuple, sort?: 'asc' | 'desc'): void {
        const model = field ? [{ field, sort }] : [];
        this.setState(
            {
                tupleTable: {
                    ...this.state.tupleTable,
                    sortModel: model,
                },
            },
            () => this.doSearch(),
        );
    }

    private doDownload() {
        this.doSearch({ download: true });
    }

    private canChangeType() {
        // not allowed for corrections
        if (this.props.correction) {
            return false;
        }

        // not allowed when editing pattern
        if (this.props.data.id) {
            return false;
        }

        // not allowed when superseding previous pattern
        return !this.props.data.supersedes;
    }

    private togglePatternForm() {
        this.setState(
            {
                formVisible: !this.state.formVisible,
            },
            () => {
                if (!this.state.formVisible) {
                    window.scrollTo(0, document.body.scrollHeight);
                }
            },
        );
    }

    private renderSupersedeElements(seamPattern: ModelWithoutId<SeamPattern>): React.ReactNode {
        if (!seamPattern.supersedes) {
            return '';
        }

        return (
            <React.Fragment>
                <Typography>Supersedes: {seamPattern.supersedes}</Typography>
                <FormControlLabel
                    disabled={undefined !== seamPattern.id}
                    className='ml-2'
                    control={
                        <Switch
                            checked={seamPattern.noReplacement}
                            onChange={(event) => this.handleChange('noReplacement', event.target.checked)}
                        />
                    }
                    label='No Replacement'
                />
            </React.Fragment>
        );
    }

    private renderStatsSelection(): React.ReactNode {
        if (this.props.hideActions) {
            return '';
        }

        return (
            <React.Fragment>
                <span>with stats relative to:&nbsp;</span>
                <FormControl sx={{ verticalAlign: 'inherit' }}>
                    <RadioGroup
                        row
                        value={this.state.withStats}
                        onChange={(evt, value) =>
                            this.setState({ withStats: value as 'none' | 'pattern' | 'store' | 'view' }, () =>
                                this.loadAggregation(this.buildListOptions(), true),
                            )
                        }
                    >
                        <Tooltip title='Do not show % (for better performance)'>
                            <FormControlLabel
                                value='none'
                                control={<Radio size='small' className='py-0' />}
                                label={<Typography variant='body2'>None</Typography>}
                            />
                        </Tooltip>
                        <Tooltip title='Main pattern data (ignore filters)'>
                            <FormControlLabel
                                value='pattern'
                                control={<Radio size='small' className='py-0' />}
                                label={<Typography variant='body2'>Pattern</Typography>}
                            />
                        </Tooltip>
                        <Tooltip title='Current matches (Pattern + Filter data)'>
                            <FormControlLabel
                                value='view'
                                control={<Radio size='small' className='py-0' />}
                                label={<Typography variant='body2'>Matches</Typography>}
                            />
                        </Tooltip>
                        <Tooltip title='Existing store matched tuples'>
                            <FormControlLabel
                                value='store'
                                control={<Radio size='small' className='py-0' />}
                                label={<Typography variant='body2'>Store Tuples</Typography>}
                            />
                        </Tooltip>
                    </RadioGroup>
                </FormControl>
            </React.Fragment>
        );
    }

    private normalizePattern(value: string) {
        if (this.props.data.type === CrisRequestType.STORE) {
            // SEAM store module does not support escape sequences.
            return value.toUpperCase();
        }
        if (
            this.props.data.data.matchOperator !== MatchType.REGEX &&
            this.props.data.data.matchOperator !== MatchType.NOT_REGEX
        ) {
            // if it is not a regex nothing to check.
            return value.toUpperCase();
        }

        const regex = /\\./g; // match \ followed by any character, uppercase everything else except these
        const matches = [...value.matchAll(regex)];
        const parts = value.split(regex);

        let result = '';
        for (let i = 0; i < parts.length; i++) {
            result += parts[i].toUpperCase();
            if (i < matches.length) {
                result += matches[i];
            }
        }

        return result;
    }

    /* eslint max-lines: off */
}

const mapStateToProps = (state: RootState) => ({
    spendSort: selectSpendSort(state),
});

const actions = { setSpendSort };

const connected = connect(mapStateToProps, actions)(PatternForm);

export { connected as PatternForm };
